import { useState, useRef, useCallback, useEffect } from 'react';

const getSupportedMimeType = () => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/wav',
    ''  // empty string = browser default
  ];
  for (const type of types) {
    if (type === '' || MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return '';
};

export const useRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const timerInterval = useRef(null);
  const startTime = useRef(null);
  const pausedDuration = useRef(0);
  const pauseStart = useRef(null);
  const streamRef = useRef(null);
  const recordedTypeRef = useRef('audio/webm');
  const wasRecordingBeforeHide = useRef(false);

  // Keep screen awake during recording using Wake Lock API
  const wakeLockRef = useRef(null);

  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('🔒 Wake lock acquired');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('🔓 Wake lock released');
        });
      }
    } catch (err) {
      console.log('Wake lock not available:', err.message);
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  };

  // Handle app going to background / screen lock
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App went to background or screen locked
        if (mediaRecorder.current && isRecording && !isPaused) {
          console.log('📱 App hidden while recording — pausing to preserve audio');
          wasRecordingBeforeHide.current = true;
          try {
            mediaRecorder.current.pause();
            setIsPaused(true);
            pauseStart.current = Date.now();
          } catch (err) {
            console.error('Error pausing recorder:', err);
          }
        }
      } else {
        // App came back to foreground
        if (wasRecordingBeforeHide.current && mediaRecorder.current) {
          wasRecordingBeforeHide.current = false;
          
          // Check if recorder is still valid
          if (mediaRecorder.current.state === 'paused') {
            console.log('📱 App visible again — recorder paused, user can resume');
            // Don't auto-resume - let user decide to resume or stop
            // Re-acquire wake lock
            requestWakeLock();
          } else if (mediaRecorder.current.state === 'inactive') {
            console.log('📱 Recorder became inactive while hidden — finalizing');
            // Recorder was killed by OS, finalize what we have
            finalizeRecording();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Capacitor-specific: handle app state changes
    const handleAppStateChange = (event) => {
      if (event.detail?.isActive === false && mediaRecorder.current && isRecording && !isPaused) {
        console.log('📱 Capacitor app backgrounded — pausing recorder');
        wasRecordingBeforeHide.current = true;
        try {
          mediaRecorder.current.pause();
          setIsPaused(true);
          pauseStart.current = Date.now();
        } catch (err) {
          console.error('Error pausing on app state change:', err);
        }
      }
    };

    window.addEventListener('appStateChange', handleAppStateChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('appStateChange', handleAppStateChange);
    };
  }, [isRecording, isPaused]);

  const finalizeRecording = useCallback(() => {
    setIsRecording(false);
    setIsPaused(false);

    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    // Build blob from whatever chunks we have
    if (audioChunks.current.length > 0) {
      const blob = new Blob(audioChunks.current, { type: recordedTypeRef.current });
      setAudioBlob(blob);
      setAudioUrl(URL.createObjectURL(blob));
      console.log(`✅ Finalized recording: ${audioChunks.current.length} chunks, ${(blob.size / 1024).toFixed(1)}KB`);
    }

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    releaseWakeLock();
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });

      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const options = mimeType ? { mimeType } : {};

      mediaRecorder.current = new MediaRecorder(stream, options);

      recordedTypeRef.current = mediaRecorder.current.mimeType || 'audio/webm';

      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunks.current.push(e.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: recordedTypeRef.current });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        console.log(`✅ Recording stopped: ${audioChunks.current.length} chunks, ${(blob.size / 1024).toFixed(1)}KB`);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        releaseWakeLock();
      };

      // Handle unexpected errors during recording
      mediaRecorder.current.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error);
        finalizeRecording();
      };

      // Request wake lock to prevent screen from sleeping
      await requestWakeLock();

      mediaRecorder.current.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      startTime.current = Date.now();
      pausedDuration.current = 0;
      pauseStart.current = null;
      wasRecordingBeforeHide.current = false;

      // Start timer
      timerInterval.current = setInterval(() => {
        if (pauseStart.current) return; // Don't update while paused
        const elapsed = Date.now() - startTime.current - pausedDuration.current;
        setDuration(Math.floor(elapsed / 1000));
      }, 100);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw error;
    }
  }, [finalizeRecording]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && isRecording) {
      // If paused, account for final pause duration
      if (pauseStart.current) {
        pausedDuration.current += Date.now() - pauseStart.current;
        pauseStart.current = null;
      }

      try {
        if (mediaRecorder.current.state !== 'inactive') {
          mediaRecorder.current.stop();
        } else {
          // Already inactive, finalize from chunks
          finalizeRecording();
        }
      } catch (err) {
        console.error('Error stopping recorder:', err);
        finalizeRecording();
      }

      setIsRecording(false);
      setIsPaused(false);
      
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
    }
  }, [isRecording, finalizeRecording]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorder.current && isRecording && !isPaused) {
      try {
        mediaRecorder.current.pause();
        setIsPaused(true);
        pauseStart.current = Date.now();
      } catch (err) {
        console.error('Error pausing recorder:', err);
      }
    }
  }, [isRecording, isPaused]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorder.current && isRecording && isPaused) {
      try {
        // Check if recorder is still valid
        if (mediaRecorder.current.state === 'paused') {
          mediaRecorder.current.resume();
          setIsPaused(false);
          pausedDuration.current += Date.now() - pauseStart.current;
          pauseStart.current = null;
          // Re-acquire wake lock
          requestWakeLock();
        } else {
          console.warn('Recorder state is:', mediaRecorder.current.state, '— cannot resume');
          // Recorder died, finalize what we have
          finalizeRecording();
        }
      } catch (err) {
        console.error('Error resuming recorder:', err);
        finalizeRecording();
      }
    }
  }, [isRecording, isPaused, finalizeRecording]);

  const resetRecording = useCallback(() => {
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    audioChunks.current = [];
    wasRecordingBeforeHide.current = false;
    releaseWakeLock();
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    isRecording,
    isPaused,
    duration,
    formattedDuration: formatDuration(duration),
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
  };
};